import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor, getter } from '@progress/kendo-data-query';
import { GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import { SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import debounce from 'lodash/debounce';
import ApiGrid, { ApiGridHandle } from '@components-clean/core/api-grid';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import CoreSplitter from '@components-clean/core/core-splitter';
import { GridHeaderTitle, GridProps, RowMenuAction } from '@components-clean/core/grid/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import {
    getExportChildFileName,
    getExportFileName,
} from '@components-clean/subject-monitoring/subject-observations/subject-monitoring-file-name-helper';
import { ApiGridParams, ObservationRecord } from '@interfaces/central-monitoring';
import {
    ChipHeaders,
    FilterContext,
    MonitoringToolKitTables,
    ObservationsSiteDto,
    ObservationType,
    SelectedRowsProps,
} from '@interfaces-clean/observations.interface';
import { SubjectActionGridRow } from '@interfaces-clean/subject-monitoring/subject-actions';
import { SubjectActionsEmailPreviewDto } from '@interfaces-clean/subject-monitoring/subject-actions-email.interface';
import {
    SubjectObservationType,
    SubjectObservationGridRow,
} from '@interfaces-clean/subject-monitoring/subject-observations';
import { SubjectReviewLog } from '@interfaces-clean/subject-monitoring/subject-review-log';
import { checkFiltersApplied } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/helpers/filter-helper';
import AddSmObservationDialog from '@pages-clean/subject-monitoring/components/dialogs/add-sm-observation-dialog';
import AddSubjectActionDialog from '@pages-clean/subject-monitoring/components/dialogs/add-subject-action-dialog';
import AddSubjectReviewLogDialog from '@pages-clean/subject-monitoring/components/dialogs/add-subject-review-log-dialog';
import BulkEditSmActionsDialog from '@pages-clean/subject-monitoring/components/dialogs/bulk-edit-sm-actions-dialog';
import BulkEditSmObservationDialog from '@pages-clean/subject-monitoring/components/dialogs/bulk-edit-sm-observation-dialog';
import DeleteSubjectActionDialog from '@pages-clean/subject-monitoring/components/dialogs/delete-subject-action-dialog';
import DeleteSubjectObservationDialog from '@pages-clean/subject-monitoring/components/dialogs/delete-subject-observation-dialog';
import DeleteSubjectReviewLogDialog from '@pages-clean/subject-monitoring/components/dialogs/delete-subject-review-log-dialog';
import EditSmObservationDialog from '@pages-clean/subject-monitoring/components/dialogs/edit-sm-observation-dialog';
import EditSubjectActionDialog from '@pages-clean/subject-monitoring/components/dialogs/edit-subject-action-dialog';
import EditSubjectReviewLogDialog from '@pages-clean/subject-monitoring/components/dialogs/edit-subject-review-log-dialog';
import EmailSubjectActionsDialog from '@pages-clean/subject-monitoring/components/dialogs/email-subject-actions-dialog';
import SubjectMonitoringService from '@pages-clean/subject-monitoring/subject-hub/subject-monitoring.service';
import { useBoolean } from '@hooks/index';
import { GridFullView, GridViewOptions, SplitterOrientation } from '@utils/constants';
import { ShowError, ShowMessage, ShowSuccess } from '@utils/notification';
import SubjectActionsApi from '@apis/bff-api/subject-monitoring/subject-actions';

interface SubjectObservationsGridProps {
    orgKey: string;
    studyRef: string;
    canEdit: boolean;
    studyNumber: string;
    isHalfScreen: boolean;
}

type ObsSelectionState = {
    recordId?: string;
    selectedItemsCount: number;
    childGridParams: ApiGridParams;
    selectedField: string; // Selected record ID
    bulkUpdateRows: SelectedRowsProps[];
    selectedChildChipHeader: ChipHeaders;
    parentTableName: string;
    childTableName: string;
    childMenuActionItems: RowMenuAction[] | undefined;
    selectedChildGridTitle: GridHeaderTitle;
    parentMenuActionItems: RowMenuAction[];
    multipleRecordSelected: string;
    noRecordSelected: string;
};

const SubjectObservationsGrid = ({
    orgKey,
    studyRef,
    studyNumber,
    canEdit,
    isHalfScreen,
}: SubjectObservationsGridProps) => {
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });
    const { t } = useTranslation(['common']);

    const [subPanelHeaders, setSubPanelHeaders] = useState([
        { id: ChipHeaders.ReviewLogView, label: mtkTranslation('observationsGrid.chipHeaders.reviewLogs') },
        { id: ChipHeaders.ActionView, label: mtkTranslation('observationsGrid.chipHeaders.actions') },
    ]);

    const isChildGridSelection = useBoolean(false);
    const showAddObservationDialog = useBoolean(false);
    const showAddActionDialog = useBoolean(false);
    const showDeleteActionDialog = useBoolean(false);
    const showEditActionDialog = useBoolean(false);
    const showEditDialog = useBoolean(false);
    const showDeleteDialog = useBoolean(false);
    const showAddReviewLogDialog = useBoolean(false);
    const showEditReviewLogDialog = useBoolean(false);
    const showDeleteReviewLogDialog = useBoolean(false);
    const showFilterAppliedDialog = useBoolean(false);
    const showBulkUpdateDialog = useBoolean(false);
    const showBulkEditActionsDialog = useBoolean(false);
    const showEmailDialog = useBoolean(false);
    const [emailPreviewData, setEmailPreviewData] = useState<SubjectActionsEmailPreviewDto | null>(null);

    const previewEmailMutation = SubjectActionsApi.usePreviewEmail(studyRef);

    const divHeight = isHalfScreen ? 'calc(50vh - 75px)' : 'calc(100vh - 128px)';
    const [gridCurrentView, setGridCurrentView] = useState<GridViewOptions>(SplitterOrientation.vertical);
    const [selectedParentChipHeader, setParentSelectedChipHeader] = useState<ChipHeaders>(ChipHeaders.ObservationsView);
    const [observationToEdit, setObservationToEdit] = useState<SubjectObservationGridRow>();
    const [actionToDelete, setActionToDelete] = useState<SubjectActionGridRow>();
    const [reviewLog, setReviewLog] = useState<SubjectReviewLog | undefined>();

    const parentFilter = useRef<CompositeFilterDescriptor | null>(null);
    const childFilter = useRef<CompositeFilterDescriptor | null>(null);
    const parentChipRef = useRef<ChipHeaders>(ChipHeaders.ObservationsView);
    const childChipRef = useRef<ChipHeaders>(ChipHeaders.ReviewLogView);

    const [actionsCount, setActionsCount] = useState(0);
    const [reviewLogsCount, setReviewLogsCount] = useState(0);

    const [isReorderMode, setIsReorderMode] = useState(false);

    const [parentGridParams, setParentGridParams] = useState<ApiGridParams>({
        grid: 'SmObservations',
        studyRef,
        observationId: null,
        isChild: false,
        reviewLogId: '',
        isHistory: false,
    } as ApiGridParams);

    useEffect(() => {
        setIsReorderMode(false);
        setGridCurrentView(SplitterOrientation.vertical);
        setParentGridParams((prevState) => ({
            ...prevState,
            studyRef,
        }));
    }, [studyRef]);

    const [panes, setPanes] = useState<SplitterPaneProps[]>([{ size: '50%', min: '15px' }, { min: '35px' }]);
    const onPaneChange = (event: SplitterOnChangeEvent) => {
        debounce(() => setPanes(event.newState), 20);
    };

    const selectedTitle: GridHeaderTitle | null = {
        id: ChipHeaders.ObservationsView,
        label: mtkTranslation('observationsGrid.chipHeaders.observations'),
    };

    const parentGridRef = useRef<ApiGridHandle>(null);
    const childGridRef = useRef<ApiGridHandle>(null);
    const observationMenuActionItems = (isParentGrid: boolean = true): RowMenuAction[] => [
        {
            id: 1,
            label: mtkTranslation('observationsGrid.editObservation'),
            onClick: (dataItem: unknown) => {
                const obs = dataItem as SubjectObservationGridRow;
                setObservationToEdit(obs);
                showEditDialog.setTrue();
            },
            icon: ['far', 'pen-to-square'],
            disabled: !canEdit,
        },
        ...(isParentGrid
            ? [
                  {
                      id: 2,
                      label: mtkTranslation('observationsGrid.addAction'),
                      onClick: (dataItem: unknown) => {
                          const obs = dataItem as SubjectObservationGridRow;

                          checkFiltersApplied(
                              FilterContext.AddAction,
                              parentChipRef,
                              childChipRef,
                              parentFilter,
                              childFilter,
                              showFilterAppliedDialog,
                          );
                          setObservationToEdit(obs);
                          showAddActionDialog.setTrue();
                      },
                      icon: ['far', 'add'] as IconProp,
                      disabled: !canEdit,
                      rowDisabled: (dataItem: unknown) => {
                          const item = dataItem as ObservationsSiteDto;
                          return item.status === 'Closed';
                      },
                  },
              ]
            : []),
        ...(isParentGrid
            ? [
                  {
                      id: 3,
                      label: mtkTranslation('observationsGrid.addReviewLog'),
                      rowDisabled: (dataItem: unknown) => {
                          const item = dataItem as SubjectObservationGridRow;
                          return (
                              item.status === 'Closed' || item.subject_review_log_created_for_latest_dataset === 'Yes'
                          );
                      },
                      onClick: (dataItem: unknown) => {
                          const obs = dataItem as SubjectObservationGridRow;
                          checkFiltersApplied(
                              FilterContext.AddReviewLog,
                              parentChipRef,
                              childChipRef,
                              parentFilter,
                              childFilter,
                              showFilterAppliedDialog,
                          );
                          setObservationToEdit(obs);
                          showAddReviewLogDialog.setTrue();
                      },
                      icon: ['far', 'add'] as IconProp,
                      disabled: !canEdit,
                      showMenuItem: (dataItem: unknown) => {
                          const item = dataItem as SubjectObservationGridRow;
                          return item.type !== 'System';
                      },
                  },
              ]
            : []),
        {
            id: 4,
            label: mtkTranslation('observationsGrid.deleteObservation'),
            onClick: (dataItem: unknown) => {
                const obs = dataItem as SubjectObservationGridRow;

                checkFiltersApplied(
                    FilterContext.DeleteSubjectObservation,
                    parentChipRef,
                    childChipRef,
                    parentFilter,
                    childFilter,
                    showFilterAppliedDialog,
                );
                setObservationToEdit(obs);
                setActionsCount(parseInt(obs.open_actions, 10));
                setReviewLogsCount(parseInt(obs.reviews, 10));
                showDeleteDialog.setTrue();
            },
            icon: ['fas', 'trash-xmark'],
            colorProp: 'Red',
            disabled: !canEdit,
        },
    ];
    const actionMenuActionItems: RowMenuAction[] = [
        {
            id: 1,
            label: mtkTranslation('actionGrid.editAction'),
            onClick: (dataItem: unknown) => {
                const action = dataItem as SubjectActionGridRow;
                setActionToDelete(action);
                showEditActionDialog.setTrue();
            },
            disabled: !canEdit,
            icon: ['far', 'pen-to-square'],
        },
        {
            id: 2,
            label: mtkTranslation('actionGrid.deleteAction'),
            onClick: (dataItem: unknown) => {
                const action = dataItem as SubjectActionGridRow;
                setActionToDelete(action);
                showDeleteActionDialog.setTrue();
            },
            disabled: !canEdit,
            icon: ['fas', 'trash-xmark'],
            colorProp: 'Red',
        },
    ];
    const reviewLogMenuActionItems: RowMenuAction[] = [
        {
            id: 1,
            label: mtkTranslation('reviewLogGrid.editReviewLog'),
            onClick: (dataItem) => {
                const review = dataItem as SubjectReviewLog;
                setReviewLog(review);
                showEditReviewLogDialog.setTrue();
            },
            icon: ['far', 'pen-to-square'],
            disabled: !canEdit,
        },
        {
            id: 2,
            label: mtkTranslation('reviewLogGrid.deleteReviewLog'),
            onClick: (dataItem) => {
                const log = dataItem as SubjectReviewLog;
                setReviewLog(log);
                showDeleteReviewLogDialog.setTrue();
            },
            icon: ['fas', 'trash-xmark'],
            colorProp: 'Red',
            disabled: !canEdit,
            rowDisabled: (dataItem: unknown) => {
                const item = dataItem as SubjectReviewLog;
                return item.type !== 'Manual' || item?.is_latest_dataset_date !== 'Yes';
            },
            showMenuItem: (dataItem: unknown) => {
                const item = dataItem as SubjectReviewLog;
                return item.type === 'Manual' && item.is_first_log === 'No';
            },
        },
    ];

    const [selectionState, setSelectionState] = useState<ObsSelectionState>({
        selectedItemsCount: 0,
        childGridParams: {
            grid: 'SmReviewLog',
            studyRef,
            observationId: null,
            isChild: true,
            isHistory: false,
            reviewLogId: '',
        },
        selectedField: '',
        bulkUpdateRows: [],
        selectedChildChipHeader: ChipHeaders.ReviewLogView,
        parentTableName: MonitoringToolKitTables.SubjectObservations,
        childTableName: MonitoringToolKitTables.SubjectReviewLogs,
        childMenuActionItems: reviewLogMenuActionItems,
        selectedChildGridTitle: {
            id: ChipHeaders.ReviewLogView,
            label: mtkTranslation('observationsGrid.chipHeaders.reviewLogs'),
        },
        parentMenuActionItems: observationMenuActionItems(true),
        multipleRecordSelected: mtkTranslation('multipleSelectObservationInfo'),
        noRecordSelected: mtkTranslation('selectObservationInfo'),
    });

    const onSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        let selectedRowCount = 0;
        let selectedItem: ObservationRecord;
        isChildGridSelection.setFalse();

        let selectedItemsProps: SelectedRowsProps[] = [];
        if (Object.keys(selectedState).length !== 0) {
            const idGetter = getter('id') ?? '';

            const selectedRows = event.dataItems.filter((x) => selectedState[idGetter(x)] === true);
            selectedRowCount = selectedRows.length;
            selectedItemsProps = selectedRows.map((item: ObservationRecord) => ({
                id: idGetter(item) as number,
                hasOpenActions: item.open_actions > 0,
            }));
            selectedItem = event.dataItems.find(
                (x) => selectedState[idGetter(x)] === true,
            ) as unknown as ObservationRecord;
        } else {
            selectedItem = event.startDataItem as ObservationRecord;
        }

        if (!selectedItem) {
            setSelectionState((old) => ({
                ...old,
                selectedField: '',
                selectedItemsCount: 0,
                bulkUpdateRows: selectedItemsProps,
            }));

            return;
        }

        let selectedObservationId = selectedItem.id as unknown as string;
        let recordID = selectedItem.record_id;

        if (selectionState.parentTableName !== MonitoringToolKitTables.SubjectObservations) {
            selectedObservationId = selectedItem.observation_id as unknown as string;
            recordID = selectedItem.observation_record_id;
        }

        // actionsCount.current = selectedItem.open_actions;
        setActionsCount(selectedItem.open_actions);
        // reviewLogsCount.current = selectedItem.reviews;
        setReviewLogsCount(selectedItem.reviews);

        setSelectionState((old) => ({
            ...old,
            recordId: recordID,
            selectedItemsCount: selectedRowCount,
            childGridParams: {
                ...old.childGridParams,
                studyRef,
                observationId: selectedObservationId,
            },
            selectedField: selectedObservationId,
            bulkUpdateRows: selectedItemsProps,
        }));
    };

    const mainPanelHeaders = [
        {
            id: ChipHeaders.ObservationsView,
            label: mtkTranslation('observationsGrid.chipHeaders.observations'),
        },
        {
            id: ChipHeaders.ReviewLogView,
            label: mtkTranslation('observationsGrid.chipHeaders.reviewLogs'),
        },
        {
            id: ChipHeaders.ActionView,
            label: mtkTranslation('observationsGrid.chipHeaders.actions'),
        },
    ];

    /**
     * Sets the relevant grid parameters based on the parent chip selection.
     * - If parent chip = ObservationsView, then default child chip is ReviewLog.
     * - If parent chip is ReviewLogView, then default child chip is Observations;
     * - If parent chip is ActionView, then default child chip is Observations
     * @param chipHeader The selected chip header
     */
    const onParentChipSelected = (chipHeader: ChipHeaders): void => {
        switch (chipHeader) {
            case ChipHeaders.ObservationsView:
                setParentSelectedChipHeader(ChipHeaders.ObservationsView);
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'SmReviewLog',
                        studyRef,
                        observationId: old.selectedField,
                        isChild: true,
                        reviewLogId: '',
                        isHistory: false,
                    },
                    parentMenuActionItems: observationMenuActionItems(true),
                    selectedChildGridTitle: {
                        id: ChipHeaders.ReviewLogView,
                        label: mtkTranslation('observationsGrid.chipHeaders.observations'),
                    },
                    selectedChildChipHeader: ChipHeaders.ReviewLogView,
                    parentTableName: MonitoringToolKitTables.SubjectObservations,
                    multipleRecordSelected: mtkTranslation('multipleSelectObservationInfo'),
                    noRecordSelected: mtkTranslation('selectObservationInfo'),
                    childMenuActionItems: reviewLogMenuActionItems,
                }));
                setParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'SmObservations',
                    observationId: null,
                    isChild: false,
                    studyRef,
                }));
                setSubPanelHeaders([
                    { id: ChipHeaders.ReviewLogView, label: mtkTranslation('observationsGrid.chipHeaders.reviewLogs') },
                    { id: ChipHeaders.ActionView, label: mtkTranslation('observationsGrid.chipHeaders.actions') },
                ]);
                break;
            case ChipHeaders.ActionView:
                setParentSelectedChipHeader(ChipHeaders.ActionView);
                setParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'SmActions',
                    studyRef,
                    observationId: null,
                    isChild: false,
                }));
                setSubPanelHeaders([
                    {
                        id: ChipHeaders.ObservationsView,
                        label: mtkTranslation('observationsGrid.chipHeaders.observations'),
                    },
                    { id: ChipHeaders.ReviewLogView, label: mtkTranslation('observationsGrid.chipHeaders.reviewLogs') },
                ]);
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'SmObservations',
                        studyRef,
                        observationId: old.selectedField,
                        isChild: true,
                    },
                    parentMenuActionItems: actionMenuActionItems,
                    selectedChildGridTitle: {
                        id: ChipHeaders.ObservationsView,
                        label: mtkTranslation('observationsGrid.chipHeaders.observations'),
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    parentTableName: MonitoringToolKitTables.SubjectActions,
                    multipleRecordSelected: mtkTranslation('actionGrid.multipleSelectActionsInfo'),
                    noRecordSelected: mtkTranslation('actionGrid.selectActionInfo'),
                    childMenuActionItems: observationMenuActionItems(false),
                }));
                break;
            case ChipHeaders.ReviewLogView:
                setParentSelectedChipHeader(ChipHeaders.ReviewLogView);
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'SmObservations',
                        studyRef,
                        observationId: old.selectedField,
                        isChild: true,
                    },
                    parentMenuActionItems: reviewLogMenuActionItems,
                    parentTableName: MonitoringToolKitTables.SubjectReviewLogs,
                    multipleRecordSelected: mtkTranslation('reviewLogGrid.multipleSelectReviewLogsInfo'),
                    noRecordSelected: mtkTranslation('reviewLogGrid.selectReviewLogInfo'),
                    selectedChildGridTitle: {
                        id: ChipHeaders.ObservationsView,
                        label: mtkTranslation('observationsGrid.chipHeaders.observations'),
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    childMenuActionItems: observationMenuActionItems(false),
                }));
                setParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'SmReviewLog',
                    studyRef,
                    observationId: '',
                    reviewLogId: '',
                    isChild: false,
                    isHistory: false,
                }));
                setSubPanelHeaders([
                    {
                        id: ChipHeaders.ObservationsView,
                        label: mtkTranslation('observationsGrid.chipHeaders.observations'),
                    },
                    { id: ChipHeaders.ActionView, label: mtkTranslation('observationsGrid.chipHeaders.actions') },
                    {
                        id: ChipHeaders.ReviewLogHistory,
                        label: mtkTranslation('observationsGrid.chipHeaders.reviewLogHistory'),
                    },
                ]);
                break;
            default:
                setParentSelectedChipHeader(ChipHeaders.ObservationsView);
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                }));
                setParentGridParams((prevState) => ({
                    ...prevState,
                    // TODO: Change this to `SmObservations` with the correct URL and params
                    grid: 'Observations',
                    level: ObservationType.Site,
                    studyRef,
                    observationId: null,
                    isChild: false,
                }));
                break;
        }
    };

    const onChildChipSelected = (chipHeader: ChipHeaders): void => {
        switch (chipHeader) {
            case ChipHeaders.ReviewLogView:
                childChipRef.current = ChipHeaders.ReviewLogView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'SmReviewLog',
                        studyRef,
                        observationId: old.selectedField,
                        isChild: true,
                        isHistory: false,
                    },
                    selectedChildChipHeader: ChipHeaders.ReviewLogView,
                    childMenuActionItems: reviewLogMenuActionItems,
                    selectedChildGridTitle: {
                        id: ChipHeaders.ReviewLogView,
                        label: mtkTranslation('observationsGrid.chipHeaders.reviewLogs'),
                    },
                    childTableName: MonitoringToolKitTables.SubjectReviewLogs,
                }));
                break;
            case ChipHeaders.ReviewLogHistory:
                childChipRef.current = ChipHeaders.ReviewLogHistory;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'SmReviewLog',
                        studyRef,
                        observationId: old.selectedField,
                        isChild: true,
                        isHistory: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ReviewLogHistory,
                    childMenuActionItems: undefined,
                    selectedChildGridTitle: {
                        id: ChipHeaders.ReviewLogHistory,
                        label: mtkTranslation('observationsGrid.chipHeaders.reviewLogHistory'),
                    },
                    childTableName: MonitoringToolKitTables.SubjectReviewLogs,
                }));
                break;
            case ChipHeaders.ActionView:
                childChipRef.current = ChipHeaders.ActionView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'SmActions',
                        studyRef,
                        observationId: old.selectedField,
                        isChild: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ActionView,
                    childTableName: MonitoringToolKitTables.SubjectActions,
                    selectedChildGridTitle: {
                        id: ChipHeaders.ActionView,
                        label: mtkTranslation('observationsGrid.chipHeaders.actions'),
                    },
                    childMenuActionItems: actionMenuActionItems,
                }));
                break;
            default:
                childChipRef.current = ChipHeaders.ObservationsView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'SmObservations',
                        studyRef,
                        observationId: old.selectedField,
                        isChild: false,
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    selectedChildGridTitle: {
                        id: ChipHeaders.ObservationsView,
                        label: mtkTranslation('observationsGrid.chipHeaders.observations'),
                    },
                    childMenuActionItems: observationMenuActionItems(false),
                }));
                break;
        }
    };

    const getSplitterAlignmentValues = (): SplitterPaneProps[] | undefined => {
        if (gridCurrentView === GridFullView.full || isReorderMode) {
            return [{ size: '50%' }, { size: '50%', collapsed: true }];
        }
        return panes;
    };

    const onChildSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        if (Object.keys(selectedState).length === 0) return;
        isChildGridSelection.setTrue();
        const idGetter = getter('id') ?? '';
        const selectedRows = event.dataItems.filter((x) => selectedState[idGetter(x)] === true);
        const selectedItemsProps: SelectedRowsProps[] = selectedRows.map((item: ObservationRecord) => ({
            id: idGetter(item) as number,
            hasOpenActions: item.open_actions > 0,
        }));

        // Keep `reviewLog` in sync with the child grid's selection when it's
        // showing review logs. The "Add and Review" flow relies on this: it flips
        // the edit dialog flag, then waits for addRowAndSelect's eventual
        // selection event to populate reviewLog and mount the dialog.
        if (selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView && selectedRows.length === 1) {
            setReviewLog(selectedRows[0] as unknown as SubjectReviewLog);
        }

        setSelectionState((old) => ({
            ...old,
            bulkUpdateRows: selectedItemsProps,
        }));
    };

    const onParentGridAdd = (id: number) => {
        if (id > 0) {
            parentGridRef?.current?.addRowAndSelect(id.toString());
            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
        } else {
            ShowError(t('common:errorMessage'));
        }
        showAddObservationDialog.setFalse();
    };

    const onObsUpdated = (id: number) => {
        if (id > 0) {
            if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
                parentGridRef?.current?.addRowAndSelect(id.toString());
            }
            if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView) {
                childGridRef?.current?.refreshData();
            }
            ShowSuccess(t('actionsSuccessfulToast'));
        } else {
            ShowError(t('common:errorMessage'));
        }
        showEditDialog.setFalse();
    };

    const onObsDeleted = (removedId: string, success: boolean) => {
        if (success) {
            // Refresh Parent Review Log grid as the child Observation could have many Review Logs which will be deleted
            // We might need to keep filters
            const refreshMainGrid =
                selectedParentChipHeader === ChipHeaders.ReviewLogView ||
                selectedParentChipHeader === ChipHeaders.ActionView;

            if (refreshMainGrid) {
                parentGridRef?.current?.refreshData(false, true);
            } else {
                parentGridRef?.current?.removeRow(removedId);
            }

            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });

            setSelectionState((old) => ({
                ...old,
                selectedField: '',
            }));
        } else {
            ShowError(t('common:errorMessage'));
        }
        showDeleteDialog.setFalse();
    };

    const onChildGridAdd = useCallback(
        (id: number) => {
            if (selectionState.selectedChildChipHeader !== ChipHeaders.ActionView)
                flushSync(() => onChildChipSelected(ChipHeaders.ActionView));

            if (id > 0) {
                ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
                parentGridRef?.current?.refreshRow(observationToEdit!.id);
                childGridRef?.current?.addRowAndSelect(id.toString());
            } else {
                ShowError(t('common:errorMessage'));
            }
            showAddActionDialog.setFalse();
        },
        [selectionState.selectedChildChipHeader, t, observationToEdit, showAddActionDialog.setFalse],
    );

    const onAddReviewLog = (id: number, addAndReview?: boolean) => {
        if (selectionState.selectedChildChipHeader !== ChipHeaders.ReviewLogView)
            flushSync(() => onChildChipSelected(ChipHeaders.ReviewLogView));

        if (id > 0) {
            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            parentGridRef?.current?.refreshRow(observationToEdit!.id);

            if (addAndReview) {
                // Clear any stale reviewLog so the dialog can't briefly show the
                // previously edited row. The dialog's render guard requires
                // reviewLog to be truthy, so flipping the flag now is safe — it
                // only mounts once addRowAndSelect's selection event populates
                // reviewLog with the newly added row.
                setReviewLog(undefined);
                showEditReviewLogDialog.setTrue();
            }
            childGridRef?.current?.addRowAndSelect(id.toString());
        } else {
            ShowError(t('common:errorMessage'));
        }
        showAddReviewLogDialog.setFalse();
    };

    const onActionDelete = (success: boolean | undefined) => {
        if (success) {
            if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
                if (actionToDelete) {
                    parentGridRef?.current?.refreshRow(actionToDelete.observation_id);
                }
                childGridRef?.current?.removeRow(actionToDelete?.id as unknown as string, false);
            } else if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                childGridRef?.current?.removeRow(actionToDelete?.id as unknown as string, false);
            } else if (selectedParentChipHeader === ChipHeaders.ActionView) {
                parentGridRef?.current?.removeRow(actionToDelete?.id as unknown as string, false);
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                }));
            }
            ShowSuccess(t('actionsSuccessfulToast'));
        } else {
            ShowError(t('common:errorMessage'));
        }
        showDeleteActionDialog.setFalse();
    };

    const onActionEdit = (id: number) => {
        if (id > 0) {
            if (selectedParentChipHeader === ChipHeaders.ObservationsView && actionToDelete) {
                parentGridRef?.current?.refreshRow(actionToDelete.observation_id);
            } else if (selectedParentChipHeader === ChipHeaders.ActionView && actionToDelete) {
                parentGridRef?.current?.refreshRow(id.toString());
            }

            if (selectionState.selectedChildChipHeader === ChipHeaders.ActionView) {
                childGridRef?.current?.refreshRow(id.toString());
            }
            ShowSuccess(t('actionsSuccessfulToast'));
        } else {
            ShowError(t('common:errorMessage'));
        }
        showEditActionDialog.setFalse();
    };

    const editReviewLog = (id: number) => {
        if (id > 0) {
            if (selectedParentChipHeader === ChipHeaders.ObservationsView && reviewLog) {
                parentGridRef?.current?.refreshRow(reviewLog.observation_id);
            } else if (selectedParentChipHeader === ChipHeaders.ReviewLogView && reviewLog) {
                parentGridRef?.current?.refreshRow(id.toString());
            }

            if (selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView) {
                childGridRef?.current?.refreshRow(id.toString());
            }
            ShowSuccess(t('actionsSuccessfulToast'));
        } else {
            ShowError(t('common:errorMessage'));
        }
        showEditReviewLogDialog.setFalse();
    };

    const onReviewLogDelete = (success: boolean | undefined) => {
        if (success) {
            if (reviewLog) {
                // Refresh parent and child grids based on the selected chip headers
                if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
                    parentGridRef?.current?.refreshRow(reviewLog.observation_id);
                } else if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                    parentGridRef?.current?.refreshData(false);
                    parentGridRef?.current?.unselectRow(reviewLog.id);
                    setSelectionState((old) => ({
                        ...old,
                        selectedField: '',
                    }));
                }

                if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView) {
                    childGridRef?.current?.refreshData();
                } else if (
                    selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView ||
                    selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogHistory
                ) {
                    // Use refreshData instead of remove row, as menu actions need re-validating
                    childGridRef?.current?.refreshData(false);
                    childGridRef?.current?.unselectRow(reviewLog.id);
                }
            }

            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
        } else {
            ShowError(t('common:errorMessage'));
        }
        showDeleteReviewLogDialog.setFalse();
    };

    const handleOnBulkUpdateClicked = () => {
        if (selectionState.bulkUpdateRows.length === 0 || isChildGridSelection.value) return;
        if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
            showBulkUpdateDialog.setTrue();
        } else if (selectedParentChipHeader === ChipHeaders.ActionView) {
            showBulkEditActionsDialog.setTrue();
        }
    };

    const handleOnChildBulkUpdateClicked = () => {
        if (
            selectionState.bulkUpdateRows.length > 0 &&
            isChildGridSelection.value &&
            selectionState.selectedChildChipHeader === ChipHeaders.ActionView
        ) {
            showBulkEditActionsDialog.setTrue();
        }
    };

    const onBulkEditSubmit = () => {
        showBulkUpdateDialog.setFalse();
        parentGridRef?.current?.refreshData(false);
    };

    const onBulkEditActionsSubmit = () => {
        showBulkEditActionsDialog.setFalse();
        if (selectedParentChipHeader === ChipHeaders.ActionView) {
            parentGridRef?.current?.refreshData(false);
        } else {
            childGridRef?.current?.refreshData(false);
        }
    };

    const emailCallback: GridProps['emailCallback'] = (selectedState) => {
        if (previewEmailMutation.isMutating) {
            return;
        }

        const actionIds = Object.keys(selectedState)
            .filter((k) => selectedState[k] === true)
            .map(Number);

        if (actionIds.length === 0) {
            return;
        }

        previewEmailMutation
            .trigger(actionIds)
            .then((resp) => {
                setEmailPreviewData(resp);
                showEmailDialog.setTrue();
            })
            .catch(() => {
                ShowError(t('common:errorMessage'));
            });
    };

    useEffect(() => {
        const subscribeDefault = SubjectMonitoringService.onRefreshSubjectObservationsGrid().subscribe(
            (refreshData) => {
                if (refreshData) {
                    parentGridRef.current?.refreshData();
                }
            },
        );

        parentGridRef.current?.refreshData();

        return () => subscribeDefault.unsubscribe();
    }, []);

    return (
        <div style={{ height: divHeight }}>
            {showAddObservationDialog.value && !showFilterAppliedDialog.value && (
                <AddSmObservationDialog
                    onClose={() => showAddObservationDialog.setFalse()}
                    onSubmit={(id: number) => onParentGridAdd(id)}
                    orgKey={orgKey}
                    studyRef={studyRef}
                />
            )}
            {showBulkUpdateDialog.value && !showFilterAppliedDialog.value && (
                <BulkEditSmObservationDialog
                    studyRef={studyRef}
                    selectedRows={selectionState.bulkUpdateRows}
                    onClose={() => showBulkUpdateDialog.setFalse()}
                    onSubmit={onBulkEditSubmit}
                />
            )}
            {showBulkEditActionsDialog.value && !showFilterAppliedDialog.value && (
                <BulkEditSmActionsDialog
                    studyRef={studyRef}
                    selectedRows={selectionState.bulkUpdateRows}
                    onClose={() => showBulkEditActionsDialog.setFalse()}
                    onSubmit={onBulkEditActionsSubmit}
                />
            )}
            {showEditDialog.value && observationToEdit && !showFilterAppliedDialog.value && (
                <EditSmObservationDialog
                    studyRef={studyRef}
                    observationToEdit={observationToEdit}
                    onClose={() => showEditDialog.setFalse()}
                    onSubmit={onObsUpdated}
                />
            )}
            {showDeleteDialog.value && !showFilterAppliedDialog.value && (
                <DeleteSubjectObservationDialog
                    observationId={observationToEdit?.id as unknown as number}
                    onClose={() => {
                        showDeleteDialog.setFalse();
                    }}
                    studyRef={studyRef}
                    recordId={selectionState.recordId ?? ''}
                    onDeleteHandler={(success) => {
                        onObsDeleted(observationToEdit?.id ?? '', success ?? false);
                    }}
                    actionsCount={actionsCount}
                    reviewLogsCount={reviewLogsCount}
                />
            )}
            <CoreSplitter
                className="!k-h-full"
                orientation={SplitterOrientation.vertical}
                data-testid="subject-observations-splitter"
                alignmentValues={getSplitterAlignmentValues()}
                onChange={onPaneChange}
            >
                <div data-testid="subject-observations-pane" className="k-h-full">
                    {studyRef && (
                        <ApiGrid
                            key={selectedTitle?.id ?? ''}
                            selectedTitle={selectedTitle}
                            title={mainPanelHeaders}
                            onChipHeaderSelected={onParentChipSelected}
                            addCallback={() => showAddObservationDialog.setTrue()}
                            gridParams={parentGridParams}
                            table={selectionState.parentTableName}
                            rowMenuActions={selectionState.parentMenuActionItems}
                            selectedChipHeader={selectedParentChipHeader}
                            maxHeight="calc(100vh)"
                            testDataId="subject-observations-grid"
                            canEdit={canEdit}
                            noPadding
                            selectable={{ enabled: true, mode: 'multiple', drag: true }}
                            onSelectionChange={onSelectionChange}
                            dataItemKey="id"
                            ref={parentGridRef}
                            showBulkUpdateIcon={selectedParentChipHeader !== ChipHeaders.ReviewLogView}
                            bulkUpdateTooltip={mtkTranslation('observationsGrid.editObservations')}
                            onBulkUpdateClicked={handleOnBulkUpdateClicked}
                            addRecordTooltip={mtkTranslation('observationsGrid.addObservation')}
                            filterListener={(newFilterState) => {
                                parentFilter.current = newFilterState || null;
                            }}
                            useFilterWarning={selectionState.bulkUpdateRows.length > 0}
                            showPlaceholder={false}
                            scrollable="virtual"
                            showAddIcon={selectedParentChipHeader === ChipHeaders.ObservationsView}
                            exportFilename={getExportFileName(parentGridParams.grid, studyNumber)}
                            showSendEmailIcon={selectedParentChipHeader === ChipHeaders.ActionView}
                            emailCallback={emailCallback}
                        />
                    )}
                </div>
                <div className="k-h-full">
                    {selectionState.selectedField && selectionState.selectedItemsCount === 1 && (
                        <ApiGrid
                            key={`${selectionState.selectedChildChipHeader}_${selectionState.selectedField}`}
                            ref={childGridRef}
                            title={subPanelHeaders}
                            selectedTitle={selectionState.selectedChildGridTitle}
                            gridParams={selectionState.childGridParams}
                            table={selectionState.childTableName}
                            rowMenuActions={selectionState.childMenuActionItems}
                            maxHeight="auto"
                            canEdit={canEdit}
                            testDataId="sm-observations-child-grid"
                            selectedChipHeader={selectionState.selectedChildChipHeader}
                            onChipHeaderSelected={onChildChipSelected}
                            onSelectionChange={onChildSelectionChange}
                            isChildGrid
                            showPlaceholder={false}
                            noPadding
                            scrollable="virtual"
                            exportFilename={getExportChildFileName(
                                selectionState.childGridParams.grid,
                                studyNumber,
                                selectionState.recordId,
                            )}
                            showSendEmailIcon={selectionState.selectedChildGridTitle?.id === ChipHeaders.ActionView}
                            emailCallback={emailCallback}
                            showBulkUpdateIcon={selectionState.selectedChildGridTitle?.id === ChipHeaders.ActionView}
                            onBulkUpdateClicked={handleOnChildBulkUpdateClicked}
                            showAddIcon={false}
                            filterable={
                                selectionState.selectedChildGridTitle?.id === ChipHeaders.ReviewLogHistory ||
                                selectionState.selectedChildGridTitle?.id === ChipHeaders.ActionView ||
                                (selectionState.selectedChildGridTitle?.id === ChipHeaders.ReviewLogView &&
                                    selectedParentChipHeader !== ChipHeaders.ActionView)
                            }
                            dataItemKey="id"
                            selectable={{ enabled: true, mode: 'multiple', drag: true }}
                            filterListener={(newFilterState) => {
                                childFilter.current = newFilterState || null;
                            }}
                        />
                    )}
                    {selectionState.selectedItemsCount > 1 && (
                        <InfoField type="Info" message={selectionState.multipleRecordSelected} layout="center" />
                    )}
                    {(!selectionState.selectedField || selectionState.selectedItemsCount === 0) && (
                        <InfoField type="Info" message={selectionState.noRecordSelected} layout="center" />
                    )}
                </div>
            </CoreSplitter>
            {showFilterAppliedDialog.value && (
                <ConfirmationDialog
                    dataTestId="confirm-add-subject-observation"
                    title={t('grid.filterSortWarning.title')}
                    onSubmit={() => {
                        showFilterAppliedDialog.setFalse();
                    }}
                    onClose={() => {
                        showAddObservationDialog.setFalse();
                        showAddActionDialog.setFalse();
                        showEditDialog.setFalse();
                        showDeleteDialog.setFalse();
                        showDeleteActionDialog.setFalse();
                        showFilterAppliedDialog.setFalse();
                        showAddReviewLogDialog.setFalse();
                        showEditReviewLogDialog.setFalse();
                        showDeleteReviewLogDialog.setFalse();
                    }}
                    primaryBtnLabel={t('common:continue')}
                >
                    {t('grid.filterSortWarning.body')}
                </ConfirmationDialog>
            )}
            {showAddActionDialog.value && !showFilterAppliedDialog.value && selectionState.selectedField && (
                <AddSubjectActionDialog
                    observationId={observationToEdit!.id}
                    studyRef={studyRef}
                    onSubmit={onChildGridAdd}
                    onClose={showAddActionDialog.setFalse}
                    type={SubjectObservationType.Manual}
                />
            )}
            {showDeleteActionDialog.value && !showFilterAppliedDialog.value && actionToDelete && (
                <DeleteSubjectActionDialog
                    recordId={actionToDelete.record_id}
                    studyRef={studyRef}
                    onDeleteHandler={onActionDelete}
                    onClose={() => showDeleteActionDialog.setFalse()}
                    actionId={Number(actionToDelete.id)}
                    observationId={actionToDelete.observation_id}
                />
            )}
            {showEditActionDialog.value && !showFilterAppliedDialog.value && actionToDelete && (
                <EditSubjectActionDialog
                    action={actionToDelete as SubjectActionGridRow}
                    studyRef={studyRef}
                    onSubmit={onActionEdit}
                    onClose={() => showEditActionDialog.setFalse()}
                />
            )}
            {showAddReviewLogDialog.value && !showFilterAppliedDialog.value && selectionState.selectedField && (
                <AddSubjectReviewLogDialog
                    observationId={observationToEdit!.id}
                    studyRef={studyRef}
                    onSubmit={(id: number, addAndReview?: boolean) => onAddReviewLog(id, addAndReview)}
                    onClose={() => showAddReviewLogDialog.setFalse()}
                />
            )}
            {showEditReviewLogDialog.value && !showFilterAppliedDialog.value && reviewLog && (
                <EditSubjectReviewLogDialog
                    reviewLog={reviewLog}
                    studyRef={studyRef}
                    onSubmit={editReviewLog}
                    onClose={showEditReviewLogDialog.setFalse}
                />
            )}
            {showDeleteReviewLogDialog.value && !showFilterAppliedDialog.value && reviewLog && (
                <DeleteSubjectReviewLogDialog
                    recordId={reviewLog.record_id}
                    studyRef={studyRef}
                    onDeleteHandler={onReviewLogDelete}
                    onClose={() => showDeleteReviewLogDialog.setFalse()}
                    reviewLogId={Number(reviewLog.id)}
                    observationId={reviewLog.observation_id}
                    reviewLogDatasetDate={reviewLog?.dataset}
                />
            )}
            {showEmailDialog.value && (
                <EmailSubjectActionsDialog
                    studyRef={studyRef}
                    studyNumber={studyNumber}
                    previewData={emailPreviewData}
                    onClose={() => {
                        showEmailDialog.setFalse();
                        setEmailPreviewData(null);
                    }}
                />
            )}
        </div>
    );
};

export default SubjectObservationsGrid;
